import React, { useState, useRef, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { formatCurrency } from '../../utils/formatUtils';
import ExpandableText from '../../modules/Control/ExpandableText';
import Swal from 'sweetalert2';

const PostList = ({ 
  posts, 
  users, 
  searchTerm, 
  setSearchTerm, 
  sortBy, 
  sortOrder, 
  setSortBy, 
  setSortOrder, 
  onEdit, 
  onDelete 
}) => {
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
  const tableContainerRef = useRef(null);

  const getUserDisplayName = (email) => users[email] || email;

  const filteredPosts = posts.filter((post) =>
    Object.values(post).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    ) || getUserDisplayName(post.usuario).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'fecha') {
      return sortOrder === 'asc' ? new Date(a.fecha) - new Date(b.fecha) : new Date(b.fecha) - new Date(a.fecha);
    } else if (sortBy === 'obra') {
      return sortOrder === 'asc' 
        ? a.obra.localeCompare(b.obra, undefined, { sensitivity: 'base' })
        : b.obra.localeCompare(a.obra, undefined, { sensitivity: 'base' });
    } else if (sortBy === 'costo') {
      return sortOrder === 'asc' ? a.costo - b.costo : b.costo - a.costo;
    }
    return 0;
  });

  const handleSort = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('-');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleScroll = () => {
    if (tableContainerRef.current.scrollTop > 300) {
      setIsScrollTopVisible(true);
    } else {
      setIsScrollTopVisible(false);
    }
  };

  const scrollToTop = () => {
    tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleDelete = (postId) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "No podrás revertir esta acción",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(postId);
        Swal.fire(
          'Eliminado',
          'El post ha sido eliminado.',
          'success'
        )
      }
    })
  };

  return (
    <>
      <div className="controls">
        <div className="search-container">
          <div className="inp">
            <input
              type="text"
              id="inp"
              placeholder="&nbsp;"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="label">Buscar...</span>
            <span className="focus-bg"></span>
          </div>
          <select onChange={handleSort} className="sort-select">
            <option value="fecha-desc">Fecha (Más reciente)</option>
            <option value="fecha-asc">Fecha (Más antiguo)</option>
            <option value="obra-asc">Proyecto (A-Z)</option>
            <option value="obra-desc">Proyecto (Z-A)</option>
            <option value="costo-asc">Inversión (Menor a Mayor)</option>
            <option value="costo-desc">Inversión (Mayor a Menor)</option>
          </select>
        </div>
      </div>

      <div 
        className="table-container-office" 
        ref={tableContainerRef} 
        style={{ maxHeight: '400px', overflowY: 'auto' }}
      >
        <table>
          <thead>
            <tr className="header-row-area">
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Proyecto</th>
              <th>Inversion total</th>
              <th>Descripción</th>
              <th>Estado</th>           
              <th>Avance</th>
              <th>Gráfico</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedPosts.map((post) => (
              <tr className='table-row' key={post.id}>
                <td>{post.fecha}</td>
                <td>{getUserDisplayName(post.usuario)}</td>
                <td className="obra-cell">
                  <div className="obra-content">{post.obra}</div>
                </td>
                <td>{formatCurrency(post.costo)}</td>
                <td>
                    <ExpandableText text={post.descripcion} />
                </td>
                <td>{post.state}</td>
                <td>{post.percentage}%</td>
                <td>
                  <PieChart width={80} height={80}>
                    <Pie
                      data={[ 
                        { name: 'Porcentaje', value: post.percentage || 0 }, 
                        { name: 'Resto', value: 100 - (post.percentage || 0) }
                      ]}
                      cx="50%" cy="50%" outerRadius={30} dataKey="value"
                    >
                      <Cell key="Porcentaje" fill="#82ca9d" />
                      <Cell key="Resto" fill="#ccc" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="edit-button" onClick={() => onEdit(post.id)}><HiOutlinePencil /></button>
                    <button className="delete-button" onClick={() => handleDelete(post.id)}><HiOutlineTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isScrollTopVisible && (
        <button 
          className="scroll-to-top-button" 
          onClick={scrollToTop} 
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '18px'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default PostList;

